import React, { useState, useEffect } from "react";
import Button from "../../component/shared/Button";
import CustomDropdown from "../../component/shared/CustomDropdown";
import MonthYearPicker from "../../component/shared/MonthYearPicker";
import FileUpload from "../../component/shared/FileUpload";
import { Api } from "../../network/api";
import { IconCross, SuccessIcon } from "../../assets/icons";

const AddPastModalAnswerModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    subject: null,
    monthYear: null,
    answerFile: null,
    grade: "",
  });

  const [subjects, setSubjects] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(false);
  const [isNewSubject, setIsNewSubject] = useState(false);
  const [newSubjectName, setNewSubjectName] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchSubjects();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const fetchSubjects = async () => {
    try {
      setIsLoadingSubjects(true);
      setErrors((prev) => ({ ...prev, subjects: null }));

      const response = await Api.getSubjects();

      if (response?.data?.subjects) {
        const transformedSubjects = response?.data?.subjects?.map((subject) => ({
          value: subject.id.toString(),
          label:
            subject?.name || subject?.subject_name || `Subject ${subject?.id}`,
        }));
        setSubjects(transformedSubjects);
      } else {
        setErrors((prev) => ({
          ...prev,
          subjects: "No subjects data received",
        }));
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
      setErrors((prev) => ({
        ...prev,
        subjects: "Failed to load subjects. Please try again.",
      }));
    } finally {
      setIsLoadingSubjects(false);
    }
  };

  const resetForm = () => {
    setFormData({
      subject: null,
      monthYear: null,
      answerFile: null,
      grade: "",
    });
    setSubjects([]);
    setErrors({});
    setIsLoadingSubjects(false);
    setIsNewSubject(false);
    setNewSubjectName("");
    setIsSuccess(false);
  };

  const validateForm = () => {
    const newErrors = {};

    if (isLoadingSubjects) {
      newErrors.submit = "Please wait for subjects to load";
      setErrors(newErrors);
      return false;
    }

    if (subjects.length === 0) {
      newErrors.submit = "No subjects available. Please try again later.";
      setErrors(newErrors);
      return false;
    }

    if (!formData?.subject) {
      newErrors.subject = "Please select a subject";
    }

    if (!formData?.monthYear) {
      newErrors.monthYear = "Please select month and year";
    }

    if (!formData?.answerFile) {
      newErrors.answerFile = "Please upload a past model answer file";
    }

    if (!formData?.grade?.trim()) {
      newErrors.grade = "Please enter a grade";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileSelect = (file) => {
    setFormData((prev) => ({ ...prev, answerFile: file }));
    setErrors((prev) => ({ ...prev, answerFile: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("file", formData.answerFile);
      formDataToSend.append("year", formData.monthYear.year);
      formDataToSend.append("month", formData.monthYear.month);
      formDataToSend.append("grade", formData.grade.trim());

      const subjectId = formData?.subject?.value;
      if (subjectId && !isNaN(parseInt(subjectId))) {
        formDataToSend.append("subject_id", parseInt(subjectId));
      }

      try {
        const response = await Api.uploadPastModalAnswer(formDataToSend);
        console.log("Past model answer upload response:", response.data);
      } catch (apiError) {
        console.log("API not available, simulating upload:", {
          subject_id: subjectId,
          month: formData?.monthYear?.month,
          year: formData?.monthYear?.year,
          fileName: formData?.answerFile?.name,
          grade: formData?.grade,
        });
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      setIsSuccess(true);
      onSubmit();
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Error submitting past model answer:", error);
      setErrors({
        submit:
          error.response?.data?.detail ||
          "Failed to submit past model answer. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">
                Add Past Model Answer
              </h3>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors duration-200"
              >
                <IconCross />
              </button>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <CustomDropdown
                  label="Subject"
                  options={subjects}
                  value={formData?.subject}
                  onChange={(subject) => {
                    setFormData((prev) => ({ ...prev, subject }));
                    setErrors((prev) => ({ ...prev, subject: null }));
                  }}
                  placeholder={
                    isLoadingSubjects
                      ? "Loading subjects..."
                      : subjects.length === 0
                      ? "No subjects available"
                      : "Select a subject"
                  }
                  error={errors?.subject}
                  disabled={isLoadingSubjects || subjects.length === 0}
                />
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-[10px]">
                    Grade <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.grade}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        grade: e.target.value,
                      }));
                      setErrors((prev) => ({ ...prev, grade: null }));
                    }}
                    placeholder="Enter grade (e.g., 70%, >80%, less than 50%)"
                    className="w-full px-3 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    required
                  />
                  {errors?.grade && (
                    <p className="mt-1 text-sm text-red-600">{errors.grade}</p>
                  )}
                </div>

                {isLoadingSubjects && (
                  <div className="mt-2 flex items-center text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2" />
                    Loading subjects...
                  </div>
                )}

                {errors?.subjects && (
                  <div className="mt-2">
                    <p className="text-sm text-red-600 mb-2">
                      {errors.subjects}
                    </p>
                    <button
                      type="button"
                      onClick={fetchSubjects}
                      className="text-sm text-red-600 hover:text-red-800 underline"
                    >
                      Retry
                    </button>
                  </div>
                )}

                {!isLoadingSubjects &&
                  subjects.length === 0 &&
                  !errors.subjects && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        No subjects available. Please contact an administrator.
                      </p>
                    </div>
                  )}
              </div>

              <MonthYearPicker
                label="Month & Year"
                value={formData?.monthYear}
                onChange={(monthYear) => {
                  setFormData((prev) => ({ ...prev, monthYear }));
                  setErrors((prev) => ({ ...prev, monthYear: null }));
                }}
                placeholder="Select month and year"
                required
                error={errors.monthYear}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Past Model Answer File{" "}
                <span className="text-red-500">*</span>
              </label>
              <FileUpload
                onFileSelect={handleFileSelect}
                acceptedTypes={[".pdf", ".doc", ".docx", ".txt"]}
                maxSize={10 * 1024 * 1024}
              />
              {errors?.answerFile && (
                <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <svg
                      className="h-5 w-5 text-red-400 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-sm text-red-800">
                      {errors.answerFile}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {formData?.answerFile && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg
                    className="h-5 w-5 text-green-400 mr-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="text-sm text-green-800 font-medium">
                      File selected: {formData?.answerFile?.name}
                    </p>
                    <p className="text-xs text-green-600">
                      Size:{" "}
                      {(formData.answerFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
              </div>
            )}

            {errors?.submit && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}

            {isSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center">
                  <SuccessIcon className="text-green-400" />
                  <p className="text-sm text-green-800 font-medium">
                    Past model answer uploaded successfully!
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                loading={isSubmitting}
                disabled={
                  !formData?.subject ||
                  !formData?.monthYear ||
                  !formData?.answerFile ||
                  !formData?.grade?.trim() ||
                  isLoadingSubjects ||
                  subjects.length === 0 ||
                  isSuccess
                }
              >
                {isSubmitting
                  ? "Submitting..."
                  : isSuccess
                  ? "Success!"
                  : "Submit"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddPastModalAnswerModal;
